/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx } from "@emotion/core"
import { FC } from "react"
import { useSelector } from "react-redux"
import { Row, Col, Card, Checkbox, Dropdown } from "@duxton-web/ui"
import { EditableFilled, MoreVerticalUniversal } from "@duxton-web/icons"
import { AppState } from "store/store.type"
import { Modifier, ModifierGroup } from "types/models/menu"
import { MenuOverviewRow, SortableItemList } from "components/common"
import { useTranslationsMap } from "common/utils/i18n"
import { selectMultiple } from "utils/modifier_group_helper"
import { getUCMConfig } from "utils/config"
import commonStyles from "styles/common.modules.css"
import { MenuDepartmentRowLoading } from "../menuOverview/lists/MenuDepartmentRowLoading"

interface ModifierGroupListProps {
  onClickModifierGroup(modifierGroup: ModifierGroup): void
  onChangeCheckboxModifierGroup(index: number): void
  onToggleShowCheckboxModifierGroup(visible: boolean): void
  onClickEditModifier(modifierGroup?: ModifierGroup): void
  modifierGroups: ModifierGroup[]
  checkedModifierGroups: ModifierGroup[]
  checkedModifiers: Modifier[]
  selectedModifierGroup: ModifierGroup
  showCheckbox: boolean
  isLoading?: boolean
}

const ModifierGroupList: FC<ModifierGroupListProps> = ({
  onClickModifierGroup,
  onChangeCheckboxModifierGroup,
  onToggleShowCheckboxModifierGroup,
  onClickEditModifier,
  modifierGroups,
  selectedModifierGroup,
  showCheckbox,
  checkedModifierGroups,
  checkedModifiers,
  isLoading = false,
}: ModifierGroupListProps) => {
  const { isSellingTimeStructure } = useSelector(
    (state: AppState) => state.menu,
  )

  const textMap = useTranslationsMap({
    header: "menu.modifierGroup.header",
    selectMultiple: "menu.actions.selectMultiple",
  })

  const isSelectedModifierGroup = (modifierGroup: ModifierGroup) =>
    selectedModifierGroup?.modifierGroupID === modifierGroup?.modifierGroupID

  const renderRow = (modifierGroup: ModifierGroup, index: number) => (
    <MenuOverviewRow
      name={modifierGroup?.modifierGroupName ?? ""}
      isActive={isSelectedModifierGroup(modifierGroup)}
      count={modifierGroup?.modifiers?.length ?? 0}
      countNotAvailable={
        modifierGroup?.modifiers?.filter((m: any) => m.availableStatus !== 1)
          ?.length ?? 0
      }
      showItemSelection={showCheckbox}
      renderAfter={
        <Row
          className={`show-hover-container ${showCheckbox ? "show" : ""}`}
          wrap={false}
        >
          {isSellingTimeStructure && (
            <Col className={`editBtn ${showCheckbox ? "" : "show"}`}>
              <EditableFilled
                data-testid="modifier-group-edit"
                key="edit"
                onClick={() => onClickEditModifier(modifierGroup)}
              />
            </Col>
          )}
          <Col className={`checkbox-container ${showCheckbox ? "show" : ""}`}>
            <Checkbox
              data-testid="modifier-group-checkbox"
              checked={
                !!checkedModifierGroups.find(
                  checkedModifierGroup =>
                    checkedModifierGroup.modifierGroupID ===
                    modifierGroup.modifierGroupID,
                )
              }
              indeterminate={
                !checkedModifierGroups.find(
                  checkedModifierGroup =>
                    checkedModifierGroup.modifierGroupID ===
                    modifierGroup.modifierGroupID,
                ) &&
                checkedModifiers.some(checkedModifier =>
                  modifierGroup.modifiers.find(
                    modifier =>
                      modifier.modifierID === checkedModifier.modifierID,
                  ),
                )
              }
              onChange={() => {
                onChangeCheckboxModifierGroup(index)
              }}
            />
          </Col>
        </Row>
      }
    />
  )

  // Show loading state when isLoading is true
  if (isLoading) {
    return (
      <Card
        data-testid="card-container"
        className={`${commonStyles.menuTableCard} ${commonStyles.left}`}
        title={
          <Row>
            <Col>{textMap.header}</Col>
          </Row>
        }
      >
        <MenuDepartmentRowLoading />
      </Card>
    )
  }

  return (
    <Card
      data-testid="card-container"
      className={`${commonStyles.menuTableCard} ${commonStyles.left}`}
      title={
        <Row align="middle" justify="space-between">
          <Col>{textMap.header}</Col>
          {getUCMConfig("enableTableMenuButton") && (
            <Col>
              <Dropdown
                data-testid="menu-button-dropdown"
                menu={{
                  items: [
                    {
                      key: selectMultiple,
                      label: textMap.selectMultiple,
                      onClick: () => onToggleShowCheckboxModifierGroup(true),
                    },
                  ],
                }}
                trigger={["click"]}
              >
                <MoreVerticalUniversal />
              </Dropdown>
            </Col>
          )}
        </Row>
      }
    >
      <SortableItemList
        items={modifierGroups}
        renderRowContent={renderRow}
        useDragHandle
        isItemSelected={isSelectedModifierGroup}
        onRowClick={(e: any, modifierGroup: ModifierGroup) => {
          const isClickingCheckbox =
            e.target.closest(".modifier-checkbox") !== null

          if (!isClickingCheckbox) {
            onClickModifierGroup(modifierGroup)
          }
        }}
        draggable={false}
      />
    </Card>
  )
}

export default ModifierGroupList
